import React, { useCallback, useMemo } from "react";

type StepStatus = "completed" | "current" | "upcoming";
type Step = { description: string; title?: string; onClick: () => void };
type StepProps = Step & { index: number; status: StepStatus };

type StepStyle = {
  link: { [k in StepStatus]: string };
  title: { [k in StepStatus]: string };
};

export const Step: React.FC<StepProps> = React.memo(
  ({ title, description, status, index, onClick }) => {
    const classes = useMemo<StepStyle>(
      () => ({
        link: {
          completed:
            "flex flex-col py-2 pl-4 border-l-4 border-primary-600 group hover:border-primary-800 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4",
          current:
            "flex flex-col py-2 pl-4 border-l-4 border-primary-600 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4",
          upcoming:
            "flex flex-col py-2 pl-4 border-l-4 border-light-300 group hover:border-light-500 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4",
        },
        title: {
          completed:
            "text-xs font-semibold uppercase text-primary-600 group-hover:text-primary-800",
          current: "text-xs font-semibold uppercase text-primary-600",
          upcoming:
            "text-xs font-semibold text-dark-300 uppercase group-hover:text-dark-700",
        },
      }),
      [status]
    );

    return (
      <li
        className="md:flex-1"
        role="button"
        aria-disabled={status === "upcoming"}
        onClick={status === "upcoming" ? undefined : onClick}
      >
        <div className={classes.link[status]}>
          <span className={classes.title[status]}>
            {title || `${"Step"} ${index + 1}`}
          </span>
          <span className="text-sm font-medium">{description}</span>
        </div>
      </li>
    );
  }
);

type StepperProps = { steps: Step[]; activeStepIndex: number };
const Stepper: React.FC<StepperProps> = ({ steps, activeStepIndex }) => {
  const getStepStatus = useCallback(
    (index: number): StepStatus => {
      if (index === activeStepIndex) return "current";
      if (index < activeStepIndex) return "completed";
      return "upcoming";
    },
    [activeStepIndex]
  );

  return (
    <div className="sticky bottom-0 z-10 flex flex-shrink-0 py-4 bg-white border-t border-light-100" style={{ width: "inherit"}}>
      <nav className="flex-grow max-w-6xl px-2 mx-auto">
        <ol className="space-y-4 md:flex md:space-y-0 md:space-x-8">
          {steps.map((step, index) => {
            return (
              <Step
                {...step}
                index={index}
                key={index}
                status={getStepStatus(index)}
              />
            );
          })}
        </ol>
      </nav>
    </div>
  );
};
export default React.memo(Stepper);
